/**
 * Versioned Data Manager - 版本化数据管理
 *
 * 负责管理版本化数据的更新、变化检测和批量更新
 */

/**
 * Update versioned data with lightweight versioning mechanism
 */
export function updateVersionedData<T>(
  target: { data: T; version: number; lastUpdated: number },
  newData: T,
  forceUpdate: boolean = false,
): boolean {
  // Check if update is needed
  const hasChanged = forceUpdate || hasDataChanged(newData, target.data);

  if (hasChanged) {
    // Use Object.assign to avoid recreating object
    Object.assign(target, {
      data: newData,
      version: target.version + 1,
      lastUpdated: Date.now(),
    });
    return true;
  }
  return false;
}

/**
 * Lightweight data change detection
 */
function hasDataChanged<T>(newData: T, oldData: T): boolean {
  // Simple reference comparison and length comparison
  if (newData === oldData) {
    return false;
  }

  // If objects, compare key count
  if (typeof newData === "object" && typeof oldData === "object") {
    const newKeys = Object.keys(newData as any);
    const oldKeys = Object.keys(oldData as any);

    if (newKeys.length !== oldKeys.length) {
      return true;
    }

    // Check key field changes
    for (const key of newKeys) {
      const newValue = (newData as any)[key];
      const oldValue = (oldData as any)[key];

      // If arrays, perform deep comparison
      if (Array.isArray(newValue) && Array.isArray(oldValue)) {
        if (newValue.length !== oldValue.length) {
          return true;
        }
        // Compare array contents (simple comparison, suitable for string arrays)
        for (let i = 0; i < newValue.length; i++) {
          if (newValue[i] !== oldValue[i]) {
            return true;
          }
        }
      } else if (newValue !== oldValue) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Batch update versioned data
 */
export function batchUpdateVersionedData<T>(
  target: { data: T; version: number; lastUpdated: number },
  updates: Array<{ key: string; value: any }>,
  forceUpdate: boolean = false,
): boolean {
  const newData = { ...target.data } as any;
  let hasChanges = false;

  // Apply all updates
  updates.forEach(({ key, value }) => {
    if (newData[key] !== value) {
      newData[key] = value;
      hasChanges = true;
    }
  });

  if (hasChanges || forceUpdate) {
    return updateVersionedData(target, newData, true);
  }
  return false;
}
